import React, { useEffect, useRef, useState } from 'react';
import { createChart, IChartApi, ISeriesApi, CandlestickData, LineData, Time, ColorType } from 'lightweight-charts';
import { ChartCandle, ChartTick } from '../../types/chart';
import { chartService } from '../../services/api/chartService';

export interface TradingViewChartProps {
  instrumentId: string;
  timeframe: '1s' | '1m' | '5m' | '15m' | '1h' | '1d';
  chartType: 'candlestick' | 'line';
  height?: number;
  onTimeframeChange?: (timeframe: string) => void;
  onChartTypeChange?: (chartType: 'candlestick' | 'line') => void;
}

export const TradingViewChart: React.FC<TradingViewChartProps> = ({
  instrumentId,
  timeframe,
  chartType,
  height = 400,
  onChartTypeChange
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Candlestick' | 'Line'> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize chart and load data
  useEffect(() => {
    if (!chartContainerRef.current) return;

    // Block chart initialization if no instrument is selected
    if (!instrumentId) {
      setIsLoading(false);
      setError('No instrument selected');
      return;
    }

    let isMounted = true;
    let isInitializing = false;

    const initChart = async () => {
      if (isInitializing) return;
      isInitializing = true;

      try {
        
        setIsLoading(true);
        setError(null);
        
        // Unsubscribe from previous real-time data first
        chartService.unsubscribe();
        
        // Enhanced cleanup - wait a bit for cleanup to complete
        if (chartRef.current) {
          try {
            chartRef.current.remove();
          } catch (cleanupError) {
            console.warn('Chart cleanup warning:', cleanupError);
          }
          chartRef.current = null;
          seriesRef.current = null;
        }

        // Small delay to ensure cleanup is complete
        await new Promise(resolve => setTimeout(resolve, 50));

        if (!isMounted) return;

        // Create new chart with enhanced configuration
        const container = chartContainerRef.current;
        if (!container) return;

        // Get container dimensions
        const containerRect = container.getBoundingClientRect();
        const chartWidth = Math.max(containerRect.width || 800, 400);
        const chartHeight = height || 400;
        
        const chart = createChart(container, {
          width: chartWidth,
          height: chartHeight,
          layout: {
            background: { type: ColorType.Solid, color: '#1a1a1a' },
            textColor: '#ffffff',
          },
          grid: {
            vertLines: { color: '#2a2a2a' },
            horzLines: { color: '#2a2a2a' },
          },
          crosshair: {
            mode: 1,
          },
          timeScale: {
            borderColor: '#485158',
            timeVisible: true,
            secondsVisible: false,
          },
          rightPriceScale: {
            borderColor: '#485158',
          },
        });

        if (!isMounted) return;
        chartRef.current = chart;

        // Create series with enhanced styling
        let series;
        if (chartType === 'candlestick') {
          series = chart.addCandlestickSeries({
            upColor: '#26a69a',
            downColor: '#ef5350',
            borderUpColor: '#26a69a',
            borderDownColor: '#ef5350',
            wickUpColor: '#26a69a',
            wickDownColor: '#ef5350',
          });
        } else {
          series = chart.addLineSeries({
            color: '#2196F3',
            lineWidth: 2,
          });
        }

        if (!isMounted) return;
        seriesRef.current = series;

        // Load data and setup real-time updates
        await loadData();
        setupRealTimeUpdates();

      } catch (err) {
        console.error('❌ Error initializing chart:', err);
        if (isMounted) {
          setError(`Failed to initialize chart: ${err instanceof Error ? err.message : 'Unknown error'}`);
        }
      } finally {
        isInitializing = false;
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    const loadData = async () => {
      if (!isMounted || !seriesRef.current) return;

      try {
        setIsLoading(true);
        setError(null);

        // CRITICAL: Clear all existing data first to prevent mixing with old instrument data
        if (chartType === 'candlestick') {
          (seriesRef.current as ISeriesApi<'Candlestick'>).setData([]);
        } else {
          (seriesRef.current as ISeriesApi<'Line'>).setData([]);
        }

        // Small delay to ensure clearing is visually apparent
        await new Promise(resolve => setTimeout(resolve, 100));

        if (!isMounted || !seriesRef.current) return;

        if (chartType === 'candlestick') {
          // Load candlestick data
          const candlesData = await chartService.getHistoricalCandles(instrumentId, timeframe, undefined, undefined, 100);
          
          if (!isMounted || !seriesRef.current) return;

          const chartData: CandlestickData[] = candlesData
            .filter(candle => {
              // Enhanced validation for historical candlestick data
              if (!candle || 
                  typeof candle.timestamp !== 'number' || 
                  typeof candle.open !== 'number' || 
                  typeof candle.high !== 'number' || 
                  typeof candle.low !== 'number' || 
                  typeof candle.close !== 'number' ||
                  isNaN(candle.open) ||
                  isNaN(candle.high) ||
                  isNaN(candle.low) ||
                  isNaN(candle.close)) {
                return false;
              }
              
              // Validate OHLC relationships
              if (candle.high < Math.max(candle.open, candle.close) ||
                  candle.low > Math.min(candle.open, candle.close)) {
                return false;
              }
              
              return true;
            })
            .map(candle => ({
              time: Math.floor(candle.timestamp / 1000) as Time,
              open: Number(candle.open.toFixed(5)),
              high: Number(candle.high.toFixed(5)),
              low: Number(candle.low.toFixed(5)),
              close: Number(candle.close.toFixed(5)),
            }))
            .sort((a, b) => (a.time as number) - (b.time as number)); // Ensure chronological order

          (seriesRef.current as ISeriesApi<'Candlestick'>).setData(chartData);

        } else {
          // Load line data
          const ticksData = await chartService.getHistoricalTicks(instrumentId, undefined, undefined, 200);
          
          if (!isMounted || !seriesRef.current) return;

          const chartData: LineData[] = ticksData
            .filter(tick => 
              tick && 
              typeof tick.timestamp === 'number' && 
              typeof tick.bid === 'number' &&
              typeof tick.ask === 'number'
            )
            .map(tick => ({
              time: Math.floor(tick.timestamp / 1000) as Time,
              value: (tick.bid + tick.ask) / 2,
            }))
            .sort((a, b) => (a.time as number) - (b.time as number)); // Ensure chronological order

          (seriesRef.current as ISeriesApi<'Line'>).setData(chartData);
        }

        // Fit content after data is loaded
        if (chartRef.current && isMounted) {
          chartRef.current.timeScale().fitContent();
        }

      } catch (err) {
        console.error('❌ Error loading chart data:', err);
        if (isMounted) {
          setError(`Failed to load data: ${err instanceof Error ? err.message : 'Unknown error'}`);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    const setupRealTimeUpdates = () => {
      if (!isMounted || !seriesRef.current) return;
      
      
      if (chartType === 'candlestick') {
        // Subscribe to OHLC updates for candlestick charts
        chartService.subscribeToOHLC(instrumentId, [timeframe], (newCandle: ChartCandle) => {
          if (!isMounted || !seriesRef.current) return;
                    
          // Enhanced validation for candlestick data
          if (!newCandle || 
              typeof newCandle.timestamp !== 'number' ||
              typeof newCandle.open !== 'number' ||
              typeof newCandle.high !== 'number' ||
              typeof newCandle.low !== 'number' ||
              typeof newCandle.close !== 'number' ||
              isNaN(newCandle.open) ||
              isNaN(newCandle.high) ||
              isNaN(newCandle.low) ||
              isNaN(newCandle.close)) {
            console.warn('❌ Invalid candlestick data received:', newCandle);
            return;
          }
          
          // Validate OHLC relationships
          if (newCandle.high < Math.max(newCandle.open, newCandle.close) ||
              newCandle.low > Math.min(newCandle.open, newCandle.close)) {
            console.warn('❌ Invalid OHLC relationship in candle:', newCandle);
            return;
          }
          
          try {
            const newData: CandlestickData = {
              time: Math.floor(newCandle.timestamp / 1000) as Time,
              open: Number(newCandle.open.toFixed(5)),
              high: Number(newCandle.high.toFixed(5)),
              low: Number(newCandle.low.toFixed(5)),
              close: Number(newCandle.close.toFixed(5)),
            };
            
            (seriesRef.current as ISeriesApi<'Candlestick'>).update(newData);
          } catch (updateError) {
            console.error('❌ Error updating candlestick chart:', updateError);
          }
        });
      } else {
        // Subscribe to tick updates for line charts
        chartService.subscribeToTicks(instrumentId, (newTick: ChartTick) => {
          if (!isMounted || !seriesRef.current) return;          
          const newData: LineData = {
            time: Math.floor(newTick.timestamp / 1000) as Time,
            value: (newTick.bid + newTick.ask) / 2,
          };
          
          (seriesRef.current as ISeriesApi<'Line'>).update(newData);
        });
      }
    };

    initChart();

    return () => {
      isMounted = false;
      // Unsubscribe from real-time updates
      chartService.unsubscribe();
      
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        seriesRef.current = null;
      }
    };
  }, [instrumentId, timeframe, chartType, height]);

  const handleChartTypeToggle = () => {
    const newType = chartType === 'candlestick' ? 'line' : 'candlestick';
    if (onChartTypeChange) {
      onChartTypeChange(newType);
    }
  };

  if (error) {
    return (
      <div style={{ 
        height, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#1a1a1a',
        color: '#ef5350',
        border: '1px solid #2a2a2a'
      }}>
        <div>
          <p>⚠️ {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      {/* Chart Controls */}
      <div style={{ 
        position: 'absolute', 
        top: '10px', 
        left: '10px', 
        zIndex: 10,
        display: 'flex',
        gap: '8px'
      }}>
        {/* Chart Type Toggle */}
        <button
          onClick={handleChartTypeToggle}
          style={{
            background: '#2a2a2a',
            color: 'white',
            border: '1px solid #485158',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            cursor: 'pointer'
          }}
        >
          {chartType === 'candlestick' ? '📊' : '📈'}
        </button>

        {/* Loading indicator */}
        {isLoading && (
          <div style={{
            background: '#2a2a2a',
            color: '#26a69a',
            border: '1px solid #485158',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px'
          }}>
            Loading...
          </div>
        )}
      </div>

      {/* Chart Container */}
      <div 
        ref={chartContainerRef} 
        style={{ 
          width: '100%', 
          height,
          background: '#1a1a1a',
          border: '1px solid #2a2a2a'
        }} 
      />
    </div>
  );
};

export default TradingViewChart;
